import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, ArrowRight, Calendar, User, Tag } from 'lucide-react';

const BlogPost = () => {
  const { slug } = useParams();

  // Mock blog post data - in real app, this would come from API
  const blogPosts = {
    'cybersecurity-importance': {
      title: 'The Importance of Cybersecurity in Today\'s Digital World',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1200&h=600&fit=crop',
      category: 'Cybersecurity',
      date: 'March 15, 2025',
      author: 'Sanghvi Infotech Team',
      content: `
        <p>In today's digital world, cybersecurity is more important than ever. With the increasing use of technology and the growth of online threats, businesses must prioritize security measures to protect their data and operations.</p>
        
        <h2>The Growing Threat Landscape</h2>
        <p>Cyber threats have evolved significantly over the years. From simple viruses to sophisticated ransomware attacks, businesses face a wide range of security challenges. According to recent studies, cybercrime is expected to cost businesses over $10 trillion annually by 2025.</p>
        
        <h2>Why Cybersecurity Matters</h2>
        <ul>
          <li><strong>Data Protection:</strong> Safeguarding sensitive customer and business data from unauthorized access</li>
          <li><strong>Business Continuity:</strong> Ensuring operations continue smoothly without disruption from cyber attacks</li>
          <li><strong>Reputation Management:</strong> Maintaining customer trust and brand reputation</li>
          <li><strong>Compliance:</strong> Meeting regulatory requirements and industry standards</li>
        </ul>
        
        <h2>Essential Cybersecurity Practices</h2>
        <p>Organizations should implement a multi-layered security approach that includes:</p>
        <ul>
          <li>Regular security audits and vulnerability assessments</li>
          <li>Employee training and awareness programs</li>
          <li>Advanced threat detection and response systems</li>
          <li>Data encryption and access controls</li>
          <li>Incident response planning</li>
        </ul>
        
        <h2>The Role of Managed Security Services</h2>
        <p>Many organizations are turning to managed security service providers (MSSPs) to enhance their cybersecurity posture. These providers offer 24/7 monitoring, expert threat intelligence, and rapid incident response capabilities.</p>
        
        <p>At Sanghvi Infotech, we provide comprehensive cybersecurity solutions including SIEM, SOAR, and managed SOC services to help businesses stay protected against evolving threats.</p>
      `
    },
    'system-integration': {
      title: 'System Integration and IT Infrastructure: Enhancing Business Operations',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&h=600&fit=crop',
      category: 'System Integration',
      date: 'March 12, 2025',
      author: 'Sanghvi Infotech Team',
      content: `
        <p>In today's fast-paced business world, companies rely heavily on IT infrastructure to support and streamline their operations. System integration plays a crucial role in connecting various systems and applications to work together seamlessly.</p>
        
        <h2>What is System Integration?</h2>
        <p>System integration is the process of bringing together various IT subsystems into one comprehensive system that functions as a cohesive unit. This enables different applications and systems to communicate and share data efficiently.</p>
        
        <h2>Benefits of System Integration</h2>
        <ul>
          <li><strong>Improved Efficiency:</strong> Automated workflows and reduced manual data entry</li>
          <li><strong>Better Data Visibility:</strong> Unified view of information across the organization</li>
          <li><strong>Cost Savings:</strong> Reduced operational costs and improved resource utilization</li>
          <li><strong>Enhanced Collaboration:</strong> Better communication between departments and systems</li>
        </ul>
        
        <h2>Key Components of IT Infrastructure</h2>
        <p>A robust IT infrastructure includes several critical components:</p>
        <ul>
          <li>Data centers and server infrastructure</li>
          <li>Network architecture and connectivity</li>
          <li>Storage solutions and backup systems</li>
          <li>Security infrastructure and access controls</li>
          <li>Cloud services and hybrid environments</li>
        </ul>
        
        <p>Our team at Sanghvi Infotech specializes in designing and implementing comprehensive system integration solutions tailored to your business needs.</p>
      `
    },
    'ai-ml-power': {
      title: 'Unlocking the Power of Artificial Intelligence and Machine Learning',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop',
      category: 'Technology',
      date: 'March 10, 2025',
      author: 'Sanghvi Infotech Team',
      content: `
        <p>Artificial Intelligence (AI) and Machine Learning (ML) are two of the most exciting and rapidly developing fields of technology today. They have the potential to transform industries and create new opportunities for businesses worldwide.</p>
        
        <h2>Understanding AI and ML</h2>
        <p>While often used interchangeably, AI and ML are distinct concepts. AI refers to machines capable of performing tasks that typically require human intelligence, while ML is a subset of AI that enables systems to learn and improve from experience.</p>
        
        <h2>Business Applications</h2>
        <ul>
          <li><strong>Predictive Analytics:</strong> Forecasting trends and customer behavior</li>
          <li><strong>Process Automation:</strong> Streamlining repetitive tasks and workflows</li>
          <li><strong>Customer Service:</strong> Chatbots and virtual assistants</li>
          <li><strong>Fraud Detection:</strong> Identifying suspicious patterns and anomalies</li>
        </ul>
        
        <h2>Implementation Considerations</h2>
        <p>Successful AI/ML implementation requires careful planning, quality data, and the right infrastructure. Organizations should start with clear objectives and pilot projects before scaling up.</p>
      `
    }
  };

  const post = blogPosts[slug] || blogPosts['cybersecurity-importance'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Image */}
      <div className="w-full h-96 relative">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
      </div>

      {/* Article Content */}
      <article className="container mx-auto px-6 py-12 max-w-4xl">
        <Link to="/blog">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>
        </Link>

        <Card className="border-none shadow-lg">
          <CardContent className="p-8 md:p-12">
            {/* Meta Information */}
            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-blue-600" />
                <span className="text-blue-600 font-semibold">{post.category}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{post.author}</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 leading-tight">
              {post.title}
            </h1>

            {/* Content */}
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Share & CTA */}
            <div className="mt-12 pt-8 border-t">
              <div className="bg-blue-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Need Expert Help?</h3>
                <p className="text-gray-600 mb-6">
                  Our team of experts is ready to help you implement the solutions discussed in this article. Contact us for a consultation.
                </p>
                <Link to="/contact">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </article>

      {/* Related Posts */}
      <section className="container mx-auto px-6 py-12 max-w-6xl">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {Object.entries(blogPosts).filter(([key]) => key !== slug).slice(0, 3).map(([key, relatedPost]) => (
            <Card key={key} className="overflow-hidden hover:shadow-xl transition-shadow border-none shadow-lg">
              <img src={relatedPost.image} alt={relatedPost.title} className="w-full h-48 object-cover" />
              <CardContent className="p-6">
                <span className="text-sm font-semibold text-blue-600">{relatedPost.category}</span>
                <h3 className="text-xl font-bold text-gray-900 mt-2 mb-4 line-clamp-2">{relatedPost.title}</h3>
                <Link to={`/blog/${key}`}>
                  <Button variant="link" className="px-0 text-blue-600">
                    Read More <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
};

export default BlogPost;